import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Briefcase, UserCheck, CalendarDays, Plus, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../lib/api";

const cards = [
  { key: "total", title: "Total des contrats", icon: FileText, accent: "text-slate-900", bg: "bg-slate-100", testid: "stat-total" },
  { key: "professionnel", title: "Contrats professionnels", icon: Briefcase, accent: "text-blue-600", bg: "bg-blue-50", testid: "stat-pro" },
  { key: "personnel", title: "Contrats personnels", icon: UserCheck, accent: "text-emerald-600", bg: "bg-emerald-50", testid: "stat-personnel" },
  { key: "today", title: "Créés aujourd'hui", icon: CalendarDays, accent: "text-amber-600", bg: "bg-amber-50", testid: "stat-today" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/contracts/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/contracts").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble de l'activité des contrats.</p>
        </div>
        <Link to="/generate">
          <button data-testid="dashboard-new-contract-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors duration-150">
            <Plus className="h-4 w-4" /> Nouveau contrat
          </button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ key, title, icon: Icon, accent, bg, testid }) => (
          <div key={key} data-testid={testid}
            className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</p>
              <span className={`${bg} ${accent} p-2 rounded-lg`}><Icon className="h-4 w-4" /></span>
            </div>
            <p className={`text-3xl font-extrabold tracking-tight mt-3 ${accent}`}>
              {stats ? stats[key] : "—"}
            </p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="dashboard-chart">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Contrats des 14 derniers jours</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.series || []}>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={24} />
                <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                <Bar dataKey="count" fill="#1E293B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="dashboard-recent">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Contrats récents</h2>
            <Link to="/contracts" className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid="dashboard-see-all-link">
              Tout voir <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Aucun contrat pour le moment.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.map((c) => (
                <li key={c.contract_id} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-contract-${c.contract_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.nom_complet}</p>
                    <p className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                    c.type === "professionnel" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {c.type === "professionnel" ? "Professionnel" : "Personnel"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
